import { AbstractTemplateElementComponent } from './AbstractTemplateElementComponent';
import { tag } from '../../utils/tag';
import { flipDirection } from '../../model/Snake';

export class SnakeCellsComponent extends AbstractTemplateElementComponent {
    #rootEl;
    #sizeEl;
    #wrapEl;
    #rowsList = [[]];
    #w = 0;
    #h = 0;
    #rotate = false;

    constructor(w = 10, h = 10) {
        super();

        this.#w = w;
        this.#h = h;
    }

    _render(parentNode) {
        const rootEl = tag('div', 'snake');
        const sizeEl = tag('div', 'snake__size');
        const wrapEl = tag('div', 'snake__wrap');

        rootEl.setAttribute('role', 'img');

        sizeEl.style.paddingTop = `${(100 * this.#h) / this.#w}%`;

        this.#renderInnerElements(wrapEl);

        rootEl.appendChild(sizeEl);
        rootEl.appendChild(wrapEl);
        parentNode.appendChild(rootEl);

        this.#rootEl = rootEl;
        this.#sizeEl = sizeEl;
        this.#wrapEl = wrapEl;

        this.#updateSizeStyles(this.#w, this.#h);

        return [rootEl];
    }

    #renderInnerElements(rootEl) {
        const cellsEl = tag('span', 'snake-cells');
        const rowsList = [];

        const appendRowCells = (i, num) => {
            const rowList = [];

            for (let j = 0; j < num; j += 1) {
                const c = tag('span', 'snake-cells__cell');
                const ci = tag('span', 'snake-cells__cell__inner');

                c.setAttribute('aria-hidden', true);
                c.setAttribute('data-cell-indices', `${i} ${j}`);
                c.classList.add(`snake-cells__cell--${i}-${j}`);

                c.appendChild(ci);
                cellsEl.appendChild(c);
                rowList.push(c);
            }

            return rowList;
        };

        for (let i = 0; i < this.#h - 1; i += 1) {
            rowsList.push(appendRowCells(i, this.#w));
        }
        rowsList.push(appendRowCells(this.#h - 1, this.#w));

        cellsEl.classList.add('snake__cells');

        rootEl.appendChild(cellsEl);

        this.#rowsList = rowsList;
    }

    _clear() {
        super._clear();
        this.#rootEl = null;
        this.#sizeEl = null;
        this.#wrapEl = null;
        this.#rowsList = null;
    }

    setSize(w, h) {
        if (!this.#rootEl) {
            this.#w = w;
            this.#h = h;

            return;
        }

        if (w === this.#w && h === this.#h) {
            this.#rootEl.classList.remove('snake--rotate');
            this.#rotate = false;

            this.#updateSizeStyles(this.#w, this.#h);

            return;
        }

        if (w === this.#h && h === this.#w) {
            this.#rootEl.classList.add('snake--rotate');
            this.#rotate = true;

            this.#updateSizeStyles(this.#h, this.#w);

            return;
        }

        this.#w = w;
        this.#h = h;
        this.#rootEl.classList.remove('snake--rotate');
        this.#rootEl.innerHTML = '';

        this.#renderInnerElements(this.#rootEl);
    }

    #updateSizeStyles(w, h) {
        if (!this.#rootEl) return;

        const dw = 100 / w;

        this.#sizeEl.style.paddingTop = `${dw * h}%`;
        this.#wrapEl.setAttribute('style', `--cell-width: ${dw}%; --grid-scale: ${this.#w / w};`);
    }

    updateCells(cells, cellsRemoved, headLength = 1, tailLength = 1, ateItself = false, dead = false, flipped = false) {
        if (!this.#rowsList) return;

        const harr = new Array(headLength).fill(0).map((v, i) => i);
        const tarr = new Array(tailLength).fill(0).map((v, i) => i);
        const { length } = cells;

        if (cellsRemoved instanceof Array) {
            cellsRemoved.forEach((c) => {
                let cell = this.#rowsList[flipped ? c.i : c.j];

                if (!cell) return;

                cell = cell[flipped ? c.j : c.i];

                if (!cell) return;

                cell.dataset.orientation = null;
                cell.dataset.headOrientation = null;
                cell.classList.remove('active');
                cell.classList.remove('inactive');
                cell.classList.remove('bounced');
                harr.forEach((i) => {
                    cell.classList.remove(`snake-cells__cell--head-${i}`);
                });
                tarr.forEach((i) => {
                    cell.classList.remove(`snake-cells__cell--tail-${i}`);
                });
            });
        }

        const [hcell] = cells;

        cells.forEach((c, index) => {
            let cell = this.#rowsList[flipped ? c.i : c.j];

            if (!cell) return;

            cell = cell[flipped ? c.j : c.i];

            if (!cell) return;

            cell.classList.add('active');
            cell.dataset.orientation = flipped ? flipDirection(c.orientation) : c.orientation;

            if (index > 0 && c.isBounced) {
                // Plays once
                cell.classList.add('bounced');
                cell.dataset.headOrientation = flipped ? flipDirection(hcell.orientation) : hcell.orientation;
            }

            harr.forEach((i) => {
                cell.classList.remove(`snake-cells__cell--head-${i}`);
            });
            if (index < headLength) {
                cell.classList.add(`snake-cells__cell--head-${index}`);
            }
            if (index > length - tailLength - 1) {
                cell.classList.add(`snake-cells__cell--tail-${length - index - 1}`);
            } else {
                tarr.forEach((i) => {
                    cell.classList.remove(`snake-cells__cell--tail-${i}`);
                });
            }

            if (dead) {
                cell.classList.remove('active');
                cell.classList.add('inactive'); // Means 'dead'

                if (ateItself) {
                    cell.classList.add('snake-cells__cell--ate-itself');
                }
            }
        });
    }

    clearCells() {
        if (!this.#rowsList) return;

        this.#rowsList.forEach((row, j) => {
            row.forEach((cell, i) => {
                cell.setAttribute('class', '');
                cell.classList.add('snake-cells__cell');
                cell.classList.add(`snake-cells__cell--${i}-${j}`);
            });
        });
    }

    get size() {
        if (this.#rotate) {
            return {
                w: this.#h,
                h: this.#w
            };
        }

        return {
            w: this.#w,
            h: this.#h
        };
    }

    get _elementsList() {
        return this.#rootEl ? [this.#rootEl] : [];
    }
}
